import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  earningsApi,
  EarningsBalance,
  PartnerDocument,
  PartnerDocumentType,
  PartnerSettlement,
} from '../services/earningsApi';

const aed = (n: number | null | undefined) =>
  `AED ${(n ?? 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const statusClass = (s: string) => {
  if (s === 'Paid') return 'paid';
  if (s.startsWith('Approved')) return 'progress';
  if (s === 'On hold') return 'hold';
  return 'preparing';
};

// Spec 053 AC-5 — partner-facing labels for the two commission document kinds.
const DOCUMENT_TYPE_LABEL: Record<PartnerDocumentType, string> = {
  INV: 'Commission invoice',
  CCN: 'Credit note',
};

// Spec 053 AC-21d — fallback tooltip when the backend sends no note.
const DEFAULT_CARRIED_BALANCE_NOTE =
  'A refund exceeded what your last payout could cover. The remaining amount is carried forward and deducted from your next payout until it is fully covered.';

export default function EarningsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState<EarningsBalance | null>(null);
  const [settlements, setSettlements] = useState<PartnerSettlement[]>([]);
  const [documents, setDocuments] = useState<PartnerDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [documentsError, setDocumentsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [howOpen, setHowOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [b, s] = await Promise.all([
        earningsApi.getBalance(token),
        earningsApi.listSettlements(token),
      ]);
      setBalance(b);
      setSettlements(s);
    } catch {
      setError('We could not load your earnings right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Documents load separately and non-fatally: a failure here must never blank the balance cards.
  const loadDocuments = useCallback(async () => {
    if (!token) return;
    setDocumentsLoading(true);
    setDocumentsError(false);
    try {
      setDocuments(await earningsApi.getDocuments(token));
    } catch {
      setDocuments([]);
      setDocumentsError(true);
    } finally {
      setDocumentsLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const carriedBalance = balance?.carriedBalance ?? 0;
  const awaitingTabby = balance?.awaitingTabbySettlement ?? null;

  return (
    <div className="earnings-page">
      <div className="earnings-head">
        <h1 className="earnings-title">Earnings</h1>
        <button className="earnings-how-link" onClick={() => setHowOpen(true)}>
          How payouts work
        </button>
      </div>

      {error && (
        <div className="earnings-error">
          {error} <button onClick={load}>Retry</button>
        </div>
      )}

      {/* Spec 053 AC-21d — carried shortfall, deducted from the next payout */}
      {!loading && carriedBalance > 0 && (
        <div className="earnings-owe" role="status">
          <span className="earnings-owe-icon" aria-hidden="true">!</span>
          <span className="earnings-owe-text">
            You owe <strong>{aed(carriedBalance)}</strong> — deducted from your next payout
          </span>
          <span className="earnings-tooltip">
            <button type="button" className="earnings-tooltip-trigger" aria-label="Why do I owe this?">i</button>
            <span className="earnings-tooltip-bubble" role="tooltip">
              {balance?.carriedBalanceNote || DEFAULT_CARRIED_BALANCE_NOTE}
            </span>
          </span>
        </div>
      )}

      {/* Balance cards */}
      <div className="earnings-cards">
        <div className="earnings-card accent">
          <span className="earnings-card-label">Next payout</span>
          <span className="earnings-card-value">{loading ? '—' : aed(balance?.nextPayout)}</span>
          <span className="earnings-card-sub">Collected on your next cycle close</span>
        </div>
        <div className="earnings-card">
          <span className="earnings-card-label">In progress</span>
          <span className="earnings-card-value">{loading ? '—' : aed(balance?.inProgress)}</span>
          <span className="earnings-card-sub">Approved, payment on the way</span>
        </div>
        <div className="earnings-card">
          <span className="earnings-card-label">Paid to date</span>
          <span className="earnings-card-value">{loading ? '—' : aed(balance?.paidToDate)}</span>
          <span className="earnings-card-sub">Lifetime transferred</span>
        </div>
        <div className="earnings-card hold">
          <span className="earnings-card-label">On hold</span>
          <span className="earnings-card-value">{loading ? '—' : aed(balance?.onHold)}</span>
          <span className="earnings-card-sub">
            {(balance?.onHold ?? 0) > 0 ? 'Under review — contact support' : 'Nothing on hold'}
          </span>
        </div>
      </div>

      {/* Spec 067 — Tabby orders still in the Tabby holding period. Informational: not in any total above. */}
      {!loading && awaitingTabby && awaitingTabby.count > 0 && (
        <div className="earnings-card earnings-awaiting" data-testid="awaiting-tabby">
          <span className="earnings-card-label">Awaiting Tabby settlement</span>
          <span className="earnings-awaiting-value">
            {awaitingTabby.count} {awaitingTabby.count === 1 ? 'order' : 'orders'}, {aed(awaitingTabby.grossAmount)}
            {awaitingTabby.earliestEligibleAt && <>, released from {shortDate(awaitingTabby.earliestEligibleAt)}</>}
          </span>
          <span className="earnings-card-sub">Paid with Tabby. Added to a payout once Tabby settles them.</span>
        </div>
      )}

      {/* Settlements */}
      <div className="earnings-panel">
        <h2 className="earnings-panel-title">Settlements</h2>
        {loading ? (
          <div className="earnings-empty"><div className="earnings-spinner" /></div>
        ) : settlements.length === 0 ? (
          <div className="earnings-empty">
            <p className="earnings-empty-title">No settlements yet</p>
            <p className="earnings-empty-sub">
              Your first payout will be prepared automatically once your completed orders clear their hold period.
            </p>
            <button className="earnings-how-link" onClick={() => setHowOpen(true)}>How payouts work</button>
          </div>
        ) : (
          <div className="earnings-table-wrap">
            <table className="earnings-table">
              <thead>
                <tr>
                  <th>Settlement</th>
                  <th>Period</th>
                  <th>Orders</th>
                  <th className="num">Net payout</th>
                  <th>Status</th>
                  <th>Paid</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map((s) => (
                  <tr key={s.id} onClick={() => navigate(`/earnings/${s.id}`)} className="earnings-row">
                    <td className="mono">{s.settlementNumber}</td>
                    <td>{shortDate(s.periodStart)} – {shortDate(s.periodEnd)}</td>
                    <td>{s.orderCount}</td>
                    <td className="num strong">{aed(s.netPayable)}</td>
                    <td><span className={`earnings-badge ${statusClass(s.status)}`}>{s.status}</span></td>
                    <td>{shortDate(s.paidAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Spec 053 AC-5 — commission invoices + credit notes */}
      <div className="earnings-panel">
        <h2 className="earnings-panel-title">Documents</h2>
        {documentsLoading ? (
          <div className="earnings-empty"><div className="earnings-spinner" /></div>
        ) : documentsError ? (
          <div className="earnings-error">
            We could not load your documents right now. <button onClick={loadDocuments}>Retry</button>
          </div>
        ) : documents.length === 0 ? (
          <div className="earnings-empty">
            <p className="earnings-empty-title">No documents yet</p>
            <p className="earnings-empty-sub">
              Your commission invoice is issued when a settlement is paid; a credit note appears if commission is later refunded.
            </p>
          </div>
        ) : (
          <div className="earnings-table-wrap">
            <table className="earnings-table earnings-documents">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Number</th>
                  <th>Date</th>
                  <th className="num">Amount</th>
                  <th>Settlement</th>
                  <th className="action"><span className="sr-only">Download</span></th>
                </tr>
              </thead>
              <tbody>
                {documents.map((d) => (
                  <tr key={`${d.type}-${d.number}`}>
                    <td><span className={`earnings-doc-type ${d.type.toLowerCase()}`}>{DOCUMENT_TYPE_LABEL[d.type]}</span></td>
                    <td className="mono">{d.number}</td>
                    <td>{shortDate(d.date)}</td>
                    <td className={`num strong${d.type === 'CCN' ? ' deduct' : ''}`}>{aed(d.amount)}</td>
                    <td className="mono">{d.settlementNumber ?? '—'}</td>
                    <td className="action">
                      {d.downloadUrl ? (
                        <a
                          className="earnings-order-link"
                          href={d.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </a>
                      ) : (
                        <span className="earnings-doc-pending">Preparing…</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {howOpen && <HowPayoutsWork onClose={() => setHowOpen(false)} />}
    </div>
  );
}

function HowPayoutsWork({ onClose }: { onClose: () => void }) {
  return (
    <div className="earnings-modal-backdrop" onClick={onClose}>
      <div className="earnings-modal" onClick={(e) => e.stopPropagation()}>
        <h3>How payouts work</h3>
        <ol className="earnings-how-list">
          <li><strong>You complete an order.</strong> Its earnings enter a short hold while the customer's review window is open.</li>
          <li><strong>Earnings are released.</strong> After the hold, the order joins your next payout.</li>
          <li><strong>We prepare a settlement.</strong> On your cycle (weekly, bi-weekly or monthly) we bundle your cleared orders into one statement.</li>
          <li><strong>Garagat commission &amp; VAT.</strong> Our platform commission and the 5% VAT on that commission are deducted. You receive the rest.</li>
          <li><strong>You get paid.</strong> We transfer the net amount to your bank and mark the settlement Paid, with the transfer reference.</li>
        </ol>
        <button className="earnings-btn-primary" onClick={onClose}>Got it</button>
      </div>
    </div>
  );
}
